import { UserManager, Log, User } from 'oidc-client-ts';
import {
  REACT_APP_ASURE_ID_BASE_URL,
  REACT_APP_ASURE_ID_CLIENT_ID,
  DEFAULT_TOKEN_NAME,
} from '../constants/globals';
import { setToken } from './tokens';
import { request } from './http';
// Ensure required environment variables are defined
if (!REACT_APP_ASURE_ID_BASE_URL) {
  throw new Error('REACT_APP_ASURE_ID_BASE_URL is not defined');
}
if (!REACT_APP_ASURE_ID_CLIENT_ID) {
  throw new Error('REACT_APP_ASURE_ID_CLIENT_ID is not defined');
}
// get current base url
const baseUrl = window.location.origin;
const oidcConfig = {
  // Change from token endpoint to base identity URL
  authority: REACT_APP_ASURE_ID_BASE_URL as string,
  client_id: REACT_APP_ASURE_ID_CLIENT_ID as string,
  redirect_uri: `${baseUrl}/callback`,
  scope: 'openid profile email',
  response_type: 'code',
  post_logout_redirect_uri: `${baseUrl}/asure-id`,
  automaticSilentRenew: true,
  silent_redirect_uri: `${baseUrl}/silent-renew`,
  metadata: {
    // Explicitly set the endpoints if the discovery endpoint is not working
    authorization_endpoint: REACT_APP_ASURE_ID_BASE_URL + '/oauth2/authorize',
    token_endpoint: REACT_APP_ASURE_ID_BASE_URL + '/oauth2/token',
    userinfo_endpoint: REACT_APP_ASURE_ID_BASE_URL + '/oauth2/userinfo',
    end_session_endpoint: REACT_APP_ASURE_ID_BASE_URL + '/oidc/logout',
  },
};

// Log the configuration for debugging
console.log('OIDC Configuration:', {
  authority: oidcConfig.authority,
  client_id: oidcConfig.client_id,
  redirect_uri: oidcConfig.redirect_uri,
  metadata: oidcConfig.metadata,
});

const userManager = new UserManager(oidcConfig);

// Add event listeners for debugging
userManager.events.addUserLoaded((user) => {
  console.log('User loaded:', user);
  // Update token whenever user is loaded (including after renew)
  if (user && user.access_token) {
    setToken(DEFAULT_TOKEN_NAME, user.access_token);
  }
});

userManager.events.addSilentRenewError((error) => {
  console.error('Silent renew error:', error);
});

userManager.events.addUserSignedOut(() => {
  console.log('User signed out');
});

export const login = async () => {
  try {
    console.log('Starting login redirect...');
    await userManager.signinRedirect();
  } catch (error) {
    console.error('Login redirect failed:', error);
    throw error;
  }
};

export const logout = async () => {
  try {
    await userManager.signoutRedirect();
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

export const handleCallback = async (): Promise<User> => {
  try {
    const user = await userManager.signinRedirectCallback();
    // token isn't available in the user object, so we need to fetch it
    // Store token directly on successful authentication
    if (user && user.access_token) {
      setToken(DEFAULT_TOKEN_NAME, user.access_token);
    }

    return user;
  } catch (error) {
    console.error('Authentication error:', error);

    throw error;
  }
};

// Add helper to check if user is logged in
export const getUser = async () => {
  try {
    const user = await userManager.getUser();
    return user;
  } catch (error) {
    console.error('Get user error:', error);
    return null;
  }
};

export const getUserInfo = async () => {
  // make a request to the userinfo endpoint REACT_APP_ASURE_ID_BASE_URL + '/oauth2/userinfo'
  try {
    const user = await getUser();
    if (!user || !user.access_token) {
      throw new Error('User is not authenticated');
    }
    const response = await request({
      url: `${REACT_APP_ASURE_ID_BASE_URL}/oauth2/userinfo`,
      headers: {
        Authorization: `Bearer ${user.access_token}`,
        accept: 'application/vnd.identity.v2+json',
      },
    });
    return response;
  } catch (error) {
    console.error('Silent renew error:', error);
  }
};

export const handleSilentRenew = async () => {
  try {
    await userManager.signinSilentCallback();
    // Update the stored token after renewal
    const user = await userManager.getUser();
    if (user && user.access_token) {
      setToken(DEFAULT_TOKEN_NAME, user.access_token);
    }
  } catch (error) {
    console.error('Silent renew error:', error);
  }
};
